import forge from 'node-forge';
import { encodedPassword } from './storage';

const STORAGE_KEY = 'stanza';

// ✅ Utility: Validate input before processing
function isPlainObject(value: any): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ✅ Encrypt plain string to base64
function encrypt(text: string): string {
  try {
    const key = forge.pkcs5.pbkdf2(encodedPassword, 'salt', 1000, 32);
    const iv = forge.random.getBytesSync(16);
    const cipher = forge.cipher.createCipher('AES-CBC', key);
    cipher.start({ iv });
    cipher.update(forge.util.createBuffer(text, 'utf8'));
    cipher.finish();
    const encrypted = cipher.output.getBytes();
    return forge.util.encode64(iv + encrypted);
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Encryption failed');
  }
}

// ✅ Decrypt base64 back to plain string
function decrypt(encryptedBase64: string): string {
  try {
    const encryptedBytes = forge.util.decode64(encryptedBase64);
    const iv = encryptedBytes.slice(0, 16);
    const encrypted = encryptedBytes.slice(16);
    const key = forge.pkcs5.pbkdf2(encodedPassword, 'salt', 1000, 32);
    const decipher = forge.cipher.createDecipher('AES-CBC', key);
    decipher.start({ iv });
    decipher.update(forge.util.createBuffer(encrypted));
    const result = decipher.finish();
    if (!result) throw new Error('Decryption failed');
    return decipher.output.toString();
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Decryption failed');
  }
}

// ✅ Safely get all secure data (decrypted object)
export function getAllSecureData(): Record<string, any> {
  const encrypted = localStorage.getItem(STORAGE_KEY);
  if (!encrypted) return {};
  try {
    const decrypted = decrypt(encrypted);
    const parsed = JSON.parse(decrypted);
    return isPlainObject(parsed) ? parsed : {};
  } catch (error) {
    console.warn('Returning empty object due to decryption/parse failure');
    return {};
  }
}

// ✅ Store or update a secure item
export function setSecureItem(key: string, value: any): void {
  if (typeof key !== 'string' || !key.trim()) {
    console.error('Invalid key provided to setSecureItem');
    return;
  }
  try {
    const existing = getAllSecureData();
    existing[key] = value;
    const json = JSON.stringify(existing);
    const encrypted = encrypt(json);
    localStorage.setItem(STORAGE_KEY, encrypted);
  } catch (error) {
    console.error('Failed to set secure item:', error);
  }
}

// ✅ Retrieve one item
export function getSecureItem<T = any>(key: string): T | undefined {
  if (typeof key !== 'string' || !key.trim()) {
    console.error('Invalid key provided to getSecureItem');
    return undefined;
  }
  try {
    const data = getAllSecureData();
    return data?.[key] as T;
  } catch (error) {
    console.error(`Failed to retrieve secure item "${key}":`, error);
    return undefined;
  }
}

// ✅ Remove one key from stanza
export function removeSecureItem(key: string): void {
  if (typeof key !== 'string' || !key.trim()) {
    console.error('Invalid key provided to removeSecureItem');
    return;
  }
  try {
    const data = getAllSecureData();
    if (!(key in data)) return; // no-op if key doesn't exist
    delete data[key];
    const encrypted = encrypt(JSON.stringify(data));
    localStorage.setItem(STORAGE_KEY, encrypted);
  } catch (error) {
    console.error('Failed to remove secure item:', error);
  }
}

// ✅ Clear all secure data
export function clearSecureStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear secure storage:', error);
  }
}
